import { CheckoutDetails } from "@features/checkout/types";
import { formatPercentage, formatPrice } from "@utils/helpers";
import { parseInt } from "lodash";
import { useTranslations } from "next-intl";

export function getCurrencyFormatWithFree(
  locale: string,
  currency: string,
  num: string | number,
  freeTranslation: string
) {
  if (
    (typeof num === "number" && num === 0) ||
    (typeof num === "string" && parseInt(num) === 0)
  ) {
    return freeTranslation;
  }
  return formatPrice(locale, currency, num);
}

export function getDeliveryFeesRange(
  locale: string,
  checkout_details: CheckoutDetails,
  translations?: ReturnType<
    typeof useTranslations<"Checkout.Cart.tickets.fees">
  >
) {
  const freeTranslation = translations?.("free") ?? "Free";
  const sortedDeliveryFees = checkout_details.DeliveryTypes.sort((a, b) => {
    if (a.Price < b.Price) {
      return -1;
    }
    if (a.Price > b.Price) {
      return 1;
    }
    return 0;
  });

  if (sortedDeliveryFees.length === 0) return null;

  if (
    sortedDeliveryFees[0].Price ===
    sortedDeliveryFees[sortedDeliveryFees.length - 1].Price
  ) {
    return getCurrencyFormatWithFree(
      locale,
      checkout_details.Currency.CurrencyCode,
      sortedDeliveryFees[0].Price,
      freeTranslation
    );
  }

  return `${getCurrencyFormatWithFree(
    locale,
    checkout_details.Currency.CurrencyCode,
    sortedDeliveryFees[0].Price,
    freeTranslation
  )} - ${getCurrencyFormatWithFree(
    locale,
    checkout_details.Currency.CurrencyCode,
    sortedDeliveryFees[sortedDeliveryFees.length - 1].Price,
    freeTranslation
  )}`;
}

export function getTransactionFeesRange(
  locale: string,
  checkout_details: CheckoutDetails,
  translations?: ReturnType<
    typeof useTranslations<"Checkout.Cart.tickets.fees">
  >
) {
  const freeTranslation = translations?.("free") ?? "Free";
  const percentageTypes = checkout_details.PaymentTypes.filter((type) =>
    type.Price.includes("%")
  )
    .map((type) =>
      type.PaidBy === 0 ? parseFloat(type.Price.replace("%", "")) : 0
    )
    .sort((a, b) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });
  const fixedTypes = checkout_details.PaymentTypes.filter((type) =>
    type.Price.includes("$")
  )
    .map((type) =>
      type.PaidBy === 0 ? parseFloat(type.Price.replace("$", "")) : 0
    )
    .sort((a, b) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });

  if (percentageTypes.length === 0) {
    if (fixedTypes[0] === fixedTypes[fixedTypes.length - 1]) {
      return getCurrencyFormatWithFree(
        locale,
        checkout_details.Currency.CurrencyCode,
        fixedTypes[0],
        freeTranslation
      );
    }
    return `${getCurrencyFormatWithFree(
      locale,
      checkout_details.Currency.CurrencyCode,
      fixedTypes[0],
      freeTranslation
    )} - ${getCurrencyFormatWithFree(
      locale,
      checkout_details.Currency.CurrencyCode,
      fixedTypes[fixedTypes.length - 1],
      freeTranslation
    )}`;
  }

  if (fixedTypes.length === 0) {
    if (percentageTypes[0] === percentageTypes[percentageTypes.length - 1]) {
      return `${formatPercentage(locale, percentageTypes[0])}`;
    }
    return `${formatPercentage(
      locale,
      percentageTypes[0]
    )} - ${formatPercentage(
      locale,
      percentageTypes[percentageTypes.length - 1]
    )}`;
  }

  if (percentageTypes[percentageTypes.length - 1] === 0) {
    if (
      percentageTypes[percentageTypes.length - 1] ===
      fixedTypes[fixedTypes.length - 1]
    ) {
      return freeTranslation;
    }
    return `${freeTranslation} - ${getCurrencyFormatWithFree(
      locale,
      checkout_details.Currency.CurrencyCode,
      fixedTypes[fixedTypes.length - 1],
      freeTranslation
    )}`;
  }

  if (fixedTypes[fixedTypes.length - 1] === 0) {
    if (
      percentageTypes[percentageTypes.length - 1] ===
      fixedTypes[fixedTypes.length - 1]
    ) {
      return freeTranslation;
    }
    return `${freeTranslation} - ${formatPercentage(
      locale,
      percentageTypes[percentageTypes.length - 1]
    )}`;
  }

  return translations?.("tbc") ?? "TBC";
}
